import {
  AccountInfo,
  BaseTag,
  Card,
  Col,
  Flex,
  List,
  Row,
  Text,
} from '@/components';
import { useMessage } from '@/providers';
import { ITEM_SPLITTER } from '@/utils';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';

import { ApprovalStatusSignifier } from '@/features/workflow/components/sider/details/approval/ApprovalStatusSignifier';
import {
  useApprovalDecision,
  useApprovalReview,
} from '@/features/workflow/hooks/approval';
import { useWorkflowStackSiderView } from '@/features/workflow/hooks/stack-sider-view';
import {
  type WorkflowStageModel,
  WorkflowStageTypeLabelModifier,
} from '@/features/workflow/models/workflow';
import { ApprovalDecisionForm } from '../approval/ApprovalDecisionForm';
import { ApprovalReviewForm } from '../approval/ApprovalReviewForm';

type Props = {
  stage: z.infer<typeof WorkflowStageModel>;
  hasAssignee: boolean;
};

export const StageStep: FC<Props> = ({ stage, hasAssignee }) => {
  const { t } = useTranslation();
  const stackSiderView = useWorkflowStackSiderView();

  const siderConfig = stackSiderView.lastItem?.config;

  invariant(
    siderConfig?.viewType === 'workflow-details',
    t('unreachable-code'),
  );

  const { openMessage } = useMessage();

  const { mutateAsync: reviewAsync } = useApprovalDecision();
  const { mutateAsync: requestReviewAsync } = useApprovalReview();

  return (
    <Row gutter={[16, 0]} wrap={false}>
      <Col flex="auto">
        <Row gutter={[0, 8]}>
          <Col span={24}>
            <Text variant="h5" weight="bold" text={stage.title} />
          </Col>

          <Col span={24}>
            <Flex gap={4}>
              {stage.stageType && (
                <BaseTag color={stage.stageType === 'all' ? 'red' : 'blue'}>
                  {WorkflowStageTypeLabelModifier[stage.stageType]}
                </BaseTag>
              )}
              {stage.label && <BaseTag>{stage.label.name}</BaseTag>}
            </Flex>
          </Col>

          <Col span={24}>
            <Card size="small">
              {!stage.approvals || stage.approvals.length === 0 ? (
                <Text text={t('message-no-approver')} weight="regular" />
              ) : (
                <List
                  itemLayout="horizontal"
                  style={{ marginTop: -12, marginBottom: -12 }}
                  dataSource={stage.approvals || []}
                  renderItem={(approval) => {
                    const isDecisionFormVisible =
                      approval.isMe &&
                      ((stage.stageType === 'one' &&
                        stage.status === 'reviewing') ||
                        (stage.stageType === 'all' &&
                          (stage.status === 'reviewing' ||
                            stage.status === 'rejected') &&
                          approval.state === 'reviewing'));

                    const isReviewFormVisible =
                      hasAssignee &&
                      stage.status === 'rejected' &&
                      approval.state === 'rejected';

                    const showReviewComment = !(
                      isDecisionFormVisible || isReviewFormVisible
                    );

                    return (
                      <List.Item>
                        <Row gutter={[0, 4]}>
                          <Col span={24}>
                            <ApprovalStatusSignifier
                              state={approval.state}
                              approvedAt={approval?.approvedAt}
                            />
                          </Col>

                          <Col span={24}>
                            <AccountInfo
                              iconUrl={approval.approver.iconDataUrl}
                              organizationName={approval.approver.organizations
                                ?.map((d) => d.name)
                                .join(ITEM_SPLITTER)}
                              accountName={approval.approver.name}
                            />
                          </Col>

                          {showReviewComment && approval.comment && (
                            <Col span={24}>
                              <Text text={approval.comment} weight="regular" />
                            </Col>
                          )}

                          {isDecisionFormVisible && (
                            <Col span={24}>
                              <ApprovalDecisionForm
                                initialValues={{
                                  comment: approval.comment ?? '',
                                  type:
                                    approval.state === 'rejected'
                                      ? 'reject'
                                      : undefined,
                                }}
                                onSubmit={async (values) => {
                                  await reviewAsync({
                                    ...values,
                                    id: approval.id,
                                    workflowId: siderConfig.id,
                                    stageId: stage.id,
                                  });
                                  openMessage('success', t('review-completed'));
                                }}
                              />
                            </Col>
                          )}

                          {isReviewFormVisible && (
                            <Col span={24}>
                              <ApprovalReviewForm
                                initialValues={{
                                  comment: approval.comment ?? '',
                                }}
                                onSubmit={async (values) => {
                                  await requestReviewAsync({
                                    ...values,
                                    id: approval.id,
                                    workflowId: siderConfig.id,
                                    stageId: stage.id,
                                  });
                                  openMessage('success', t('review-requested'));
                                }}
                              />
                            </Col>
                          )}
                        </Row>
                      </List.Item>
                    );
                  }}
                />
              )}
            </Card>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};
